import { useEffect } from 'react';
import { useNavbar } from '@app/atoms/app/navbar.atom.ts';
import { Line } from '@ui/common/line.tsx';
import { CartDesktopList, CartList, CartProductProps } from '@ui/cart';
import {
  OrderDeliveryDetails,
  OrderDetails,
} from '@features/order/order-details';
import QRCode from 'react-qr-code';
import { useNavigate, useParams } from 'react-router-dom';
import { useOrderService } from '../../entites/order/use-order.service.ts';
import { BreadcrumbItem, Breadcrumbs, Button } from '@heroui/react';
import { ArrowLeft2, Home2 } from 'iconsax-react';
import { UserAuthorizationCard } from '@features/profile/user-authorization-card';
import { ActiveOrders } from '@features/order/active-orders';
import { ShortCuts } from '@features/profile/short-cuts';
import { UserExitButton } from '@features/profile/ui/user-exit-button.tsx';
import { Icon } from '@iconify/react';

export const OrderPage = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const set = useNavbar();
  const navigate = useNavigate();

  const service = useOrderService({
    orderId: Number(orderId),
  });

  const data = service.order.data;

  useEffect(() => {
    set.orders();
  }, []);

  if (!data) {
    return <div></div>;
  }

  const productsPreviewList: CartProductProps[] = data.items.map((item) => ({
    image: item.product.main_photo,
    title: item.product.name,
    count: `${item.quantity} шт`,
    price: `${item.product.total_price} ₸`,
    description: item.product.category_name,
  }));

  return (
    <>
      <div className="hidden lg:block container max-w-7xl mx-auto">
        <div className="hidden lg:block mt-12">
          <Breadcrumbs>
            <BreadcrumbItem href="/">
              <Home2 size="24" color="#9CA5AC" />
            </BreadcrumbItem>
            <BreadcrumbItem color="primary">Профиль</BreadcrumbItem>
          </Breadcrumbs>
        </div>

        <div className="mt-6">
          <h1 className="hidden lg:inline text-4xl text-[#222222] font-bold">
            Профиль
          </h1>
        </div>
      </div>

      <div className="hidden lg:flex flex-row container max-w-7xl mx-auto mt-12 pb-32 gap-x-12">
        <div className="w-[375px] flex flex-col">
          <UserAuthorizationCard />

          <ActiveOrders />

          <ShortCuts />

          <UserExitButton />
        </div>

        <div className="flex-grow max-w-[850px]">
          <div className="flex flex-row items-center gap-x-4 text-2xl font-semibold text-[#222222] pb-3">
            <Button isIconOnly variant="light" onPress={() => navigate(-1)}>
              <ArrowLeft2 size="24" color="#9CA5AC" />
            </Button>
            Заказ № {data.id}
          </div>

          <OrderDetails
            order={data}
            isLoading={!service.order.data}
            isSubmitting={service.cancel.isMutating}
            onPress={service.cancel.trigger}
          />

          {data.delivery_type === 'delivery' && (
            <>
              <Line />

              <p className="text-[#222222] font-semibold mt-4">Доставка</p>

              <OrderDeliveryDetails order={data} />
            </>
          )}

          <p className="text-[#222222] font-semibold text-lg mt-4">
            Получение заказа
          </p>
          <p className="pt-4 text-[#60656C]">Покажите этот QR-код на кассе</p>

          {data.status === 2 && (
            <div className="flex flex-col bg-[#F8F9FA] p-12 rounded-2xl mt-6 mb-9 items-center">
              <Icon
                width={36}
                height={36}
                color="#1A73E8"
                icon="solar:qr-code-outline"
              />

              <p className="text-[#222222] font-semibold mt-6">
                QR-код пока недоступен
              </p>
              <p className="pt-4 text-sm text-[#60656C] text-center">
                Он появится, когда заказ будет готов к выдаче
              </p>
            </div>
          )}

          {data.status !== 2 && (
            <div className="flex flex-col">
              <div className="w-[180px] h-[180px] mx-auto mt-6 mb-4">
                <QRCode
                  size={180}
                  style={{
                    height: 'auto',
                    maxWidth: '100%',
                    width: '100%',
                  }}
                  value={data.collection_code || '0000000'}
                  viewBox={`0 0 256 256`}
                />
              </div>

              <p className="text-center pt-4 text-sm text-[#60656C]">
                или скажите код
              </p>
              <p className="text-center pt-1 pb-4 text-xl font-semibold text-[#222222]">
                {data.collection_code || '0000000'}
              </p>
            </div>
          )}

          <p className="text-[#222222] font-semibold mt-4">
            Товары в заказе: {data.items.length}
          </p>

          <CartDesktopList
            isShowPlaceholder={false}
            items={productsPreviewList}
          />
        </div>
      </div>

      <div className="lg:hidden">
        <OrderDetails
          order={data}
          isLoading={!service.order.data}
          isSubmitting={service.cancel.isMutating}
          onPress={service.cancel.trigger}
        />

        {data.delivery_type === 'delivery' && (
          <>
            <Line />

            <p className="text-[#222222] font-semibold mt-4">Доставка</p>

            <OrderDeliveryDetails order={data} />
          </>
        )}

        <Line />
        <p className="text-[#222222] font-semibold mt-4">Получение заказа</p>
        <p className="pt-3 text-sm text-[#60656C]">
          Покажите этот QR-код на кассе
        </p>

        {data.status === 2 && (
          <div className="flex flex-col bg-[#F8F9FA] p-6 rounded-2xl mt-6 mb-4 items-center">
            <Icon
              width={24}
              height={24}
              color="#1A73E8"
              icon="solar:qr-code-outline"
            />

            <p className="text-[#222222] font-semibold text-sm mt-4">
              QR-код пока недоступен
            </p>
            <p className="pt-2 text-sm text-[#60656C] max-w-72 text-center">
              Он появится, когда заказ будет готов к выдаче
            </p>
          </div>
        )}

        {data.status !== 2 && (
          <div className="flex flex-col">
            <div className="w-[180px] h-[180px] mx-auto mt-6 mb-4">
              <QRCode
                size={180}
                style={{
                  height: 'auto',
                  maxWidth: '100%',
                  width: '100%',
                }}
                value={data.collection_code || '0000000'}
                viewBox={`0 0 256 256`}
              />
            </div>

            <p className="text-center pt-4 text-sm text-[#60656C]">
              или скажите код
            </p>
            <p className="text-center pt-1 pb-4 text-xl font-semibold text-[#222222]">
              {data.collection_code || '0000000'}
            </p>
          </div>
        )}

        <Line />

        <p className="text-[#222222] font-semibold mt-4">
          Товары в заказе: {data.items.length}
        </p>

        <CartList
          isShowPlaceholder={false}
          isDivided
          items={productsPreviewList}
        />
      </div>
    </>
  );
};
